import React, { useContext, useEffect, useRef, useState } from 'react'
import { EPCRContext } from '../EPCR'
import EPCRCard from '../../../../components/Cards/EPCRCard'
import { Alert, Button, useMediaQuery, useTheme } from '@mui/material'
import NextButton from '../NextButton'
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto'
import API from '../../../../services/API'
import { UserContext } from '../../../../App'
import SingleImage from './SingleImage'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import ImageList from '@mui/material/ImageList'
import ImageListItem from '@mui/material/ImageListItem'
import Stack from '@mui/material/Stack'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import heic2any from 'heic2any'

const Attachments = () => {
  const { formValues, setFormValues, formID, setUploadState, setIsSaving } =
    useContext(EPCRContext)
  const { user } = useContext(UserContext)
  const [imageURLs, setImageURLs] = useState([])
  const imageRef = useRef()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'))

  useEffect(() => {
    if (!formValues?.attachments?.length) {
      setImageURLs({ urls: [], newImages: [] })
      return
    }

    const urls = []
    const newImages = []

    for (const image of formValues.attachments) {
      if (image.s3key) {
        urls.push(image.s3key)
      } else if (image.localPreview) {
        newImages.push(image)
      }
    }

    setImageURLs({ urls, newImages })
  }, [formValues.attachments])

  const handleImageUpload = async (e) => {
    let file = e.target.files[0] // let: HEIC is swapped for a JPEG below
    if (!file) return

    const isHeic =
      file.type === 'image/heic' ||
      file.type === 'image/heif' ||
      /\.heic$/i.test(file.name) ||
      /\.heif$/i.test(file.name)

    if (isHeic) {
      try {
        const converted = await heic2any({
          blob: file,
          toType: 'image/jpeg',
          quality: 0.85,
        })
        // heic2any may return Blob or Blob[]
        const jpegBlob = Array.isArray(converted) ? converted[0] : converted

        file = new File(
          [jpegBlob],
          file.name.replace(/\.(heic|heif)$/i, '.jpg'),
          {
            type: 'image/jpeg',
          },
        )
      } catch (err) {
        console.error('HEIC conversion failed', err)
        return
      }
    }

    try {
      setUploadState('pending')
      setIsSaving(true)

      // 1. Get signed URL and s3key
      const res = await API.post('/gets3uploadurl', {
        folder: `epcr/${formID}/attachments`,
        extension: file.type.split('/')[1],
      })
      const { url, key } = res.data

      // 2. Upload to S3
      await API.put(url, file, {
        headers: { 'Content-Type': file.type },
      })

      // 3. Update database with new attachment
      const dbRes = await API.put('/epcr/' + formID, {
        attachments: [
          ...(formValues?.attachments ?? []),
          {
            s3key: key,
            uploadedBy: user._id,
            uploadedAt: new Date(),
          },
        ],
      })

      if (dbRes.status === 200) {
        // 4. Update UI
        const localPreview = URL.createObjectURL(file)
        setFormValues((prev) => ({
          ...prev,
          attachments: [
            ...(prev?.attachments ?? []),
            {
              uploadedBy: user._id,
              uploadedAt: new Date(),
              localPreview,
              s3key: key,
            },
          ],
        }))
      }

      setUploadState('done')
    } catch (err) {
      console.error('Upload failed:', err)
      setUploadState('error')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Stack gap={2}>
      <EPCRCard>
        <Typography variant="title2">Attachments</Typography>
        {!formID ? (
          <Alert severity="info">You need to add Patient Details first</Alert>
        ) : (
          <Stack>
            <Button
              startIcon={<AddAPhotoIcon />}
              onClick={() => imageRef.current.click()}
              sx={{ cursor: 'pointer', marginY: 2 }}
              variant="contained"
              color="secondary"
            >
              Add a photo
            </Button>
            <Grid container spacing={2}>
              {imageURLs?.urls?.map((image, index) => (
                <SingleImage s3key={image} key={`s3-${index}`} />
              ))}
              {imageURLs?.newImages?.map((image, index) => (
                <SingleImage key={`local-${index}`} passedImage={image} />
              ))}
            </Grid>
          </Stack>
        )}
      </EPCRCard>

      <input
        ref={imageRef}
        accept="image/*"
        type="file"
        onChange={handleImageUpload}
        style={{ display: 'none' }}
      />
      <NextButton newModule="Plan" />
    </Stack>
  )
}

export default Attachments
